import type {
  PaginatedLoadingContextValue,
  SimpleLoadingContextValue,
  SingleLoadingContextValue,
} from "@tandemdiabetes/sugarmate-api-react-client"
import type {
  FC,
  ReactNode,
} from "react"
import {
  useContext,
  useMemo,
  createContext,
} from "react"
import { useHasChangedSince } from "src/hooks/utils/useHasChangedSince"

type UseLoadingContext = () =>
  | PaginatedLoadingContextValue
  | SimpleLoadingContextValue
  | SingleLoadingContextValue

export interface LoadingLifecycleContextValue {
  error: unknown;
  loaded: boolean;
  loading: boolean;
  willLoad: boolean;
  loadedOrErrored: boolean;
}

export const defaultLoadingLifecycleContextValue = {
  // these values are all forwarded from the loading context from sugarmate-api-react-client
  error: null,
  loaded: true,
  loading: false,

  // true if the loader is expected to run again
  willLoad: false,

  // true if the loader has completed and is not expected to run again
  loadedOrErrored: true,
}

export interface Props {
  children?: ReactNode;
  // whenever loadingDependencies change the loader will attempt to load new data
  loadingDependencies: unknown[];
  // willLoad will be false unless all loadingRequirements values are truthy
  loadingRequirements: unknown[];
}

/*
 * this function generates a Loading Lifecycle Context that captures the most
 * relevant loading state information for a Paginated, Simple, or Single
 * Loading Context
 *
 * it returns a hook for consuming the context and a context provider
 */
export function createLoadingLifecycleContextAndProvider(
  useLoadingContext: UseLoadingContext,
): [() => LoadingLifecycleContextValue, FC<Props>] {
  const genericLoadingContext = createContext<LoadingLifecycleContextValue>(
    defaultLoadingLifecycleContextValue,
  )
  const { Provider } = genericLoadingContext

  const useLoadingLifecycleContext = (): LoadingLifecycleContextValue => useContext(genericLoadingContext)

  const LoadingLifecycleContextProvider = (props: Props) => {
    const {
      loadingDependencies,
      loadingRequirements,
      children,
    } = props

    const {
      error,
      loaded,
      loading,
    } = useLoadingContext()

    // true if loadingRequirements contains no falsy values
    const passesRequirements = useMemo(
      () => !loadingRequirements.some((req) => !req),
      [loadingRequirements],
    )

    const hasChanged = useHasChangedSince(
      [
        loaded,
        loading,
        error,
      ],
      loadingDependencies,
    )
    const willLoad = !hasChanged && passesRequirements
    const loadedOrErrored = !willLoad && (loaded || !!error)

    const value = useMemo(
      () => ({
        error,
        loaded,
        loading,
        willLoad,
        loadedOrErrored,
      }),
      [
        error,
        loaded,
        loading,
        willLoad,
        loadedOrErrored,
      ],
    )

    return (
      <Provider value={value}>
        {children}
      </Provider>
    )
  }

  return [
    useLoadingLifecycleContext,
    LoadingLifecycleContextProvider,
  ]
}
